import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  HandHeart,
  Brain,
  Users,
  Sparkles,
  ClipboardList,
  Phone,
  Mail,
  Calendar,
  Coffee,
  Moon,
  Activity,
  MessageCircle,
  ArrowRight,
  Check,
  Quote,
  Compass,
  ShieldCheck,
  BookOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const CaregiversPage = () => {
  const [checked, setChecked] = useState({});

  const toggleCheck = (id) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const pillars = [
    {
      id: 'practical',
      icon: ClipboardList,
      title: 'Practical Help',
      tag: 'Logistics & Coordination',
      description:
        'From appointments to medical records, the day-to-day load of caregiving is real. We help lighten it.',
      points: [
        'Appointment scheduling & reminders',
        'Medical records organization',
        'Transportation coordination',
        'Treatment planning support',
      ],
      color: '#E07A5F',
    },
    {
      id: 'emotional',
      icon: Brain,
      title: 'Emotional Well-Being',
      tag: 'Mental Health',
      description:
        "Caregiving touches every emotion. Acknowledging what you're feeling is the first act of strength.",
      points: [
        'Counseling referrals',
        'Caregiver therapy resources',
        'Stress & burnout guidance',
        'Grief & anticipatory loss support',
      ],
      color: '#5D4E6D',
    },
    {
      id: 'selfcare',
      icon: HandHeart,
      title: 'Self-Care',
      tag: 'You Matter Too',
      description:
        'You cannot pour from an empty cup. Rest, nourishment, and boundaries are not optional — they are essential.',
      points: [
        'Respite care information',
        'Wellness & nutrition resources',
        'Sleep & boundary setting',
        'Personal time strategies',
      ],
      color: '#E07A5F',
    },
    {
      id: 'community',
      icon: Users,
      title: 'Community & Connection',
      tag: 'Ohana Support',
      description:
        'You are not alone. Connect with other caregivers who understand the journey you are walking.',
      points: [
        'Caregiver support groups',
        'Peer-to-peer connections',
        'Family communication tools',
        'Online & in-person gatherings',
      ],
      color: '#5D4E6D',
    },
    {
      id: 'spiritual',
      icon: Sparkles,
      title: 'Spiritual Support',
      tag: 'Hope & Meaning',
      description:
        'Whatever your beliefs, finding meaning and hope can be a powerful companion through hard seasons.',
      points: [
        'Pastoral care connections',
        'Mindfulness practices',
        'Cultural & spiritual traditions',
        'Meditation & reflection resources',
      ],
      color: '#E07A5F',
    },
  ];

  const checklistItems = [
    { id: 'feelings', icon: Heart, text: 'Acknowledge your feelings today — overwhelm, sadness, and guilt are normal' },
    { id: 'help', icon: HandHeart, text: 'Ask one person for help with a specific task this week' },
    { id: 'rest', icon: Moon, text: 'Get at least 7 hours of sleep tonight, even if it means asking someone to cover' },
    { id: 'move', icon: Activity, text: 'Move your body for 20 minutes — walk, stretch, or just step outside' },
    { id: 'connect', icon: MessageCircle, text: 'Reach out to a friend, family member, or fellow caregiver' },
    { id: 'pause', icon: Coffee, text: 'Take a 10-minute break with something that brings you joy' },
    { id: 'limits', icon: ShieldCheck, text: 'Set one healthy boundary around work, volunteering, or caregiving' },
    { id: 'support', icon: Phone, text: 'Reach out to a counselor, social worker, or support group if you are struggling' },
  ];

  const whenYouFeel = [
    {
      feeling: 'Overwhelmed',
      icon: Compass,
      action: 'Pause. Write down only the three most important things for today. Everything else can wait.',
      cta: 'Get help from us',
      to: '/contact',
    },
    {
      feeling: 'Isolated',
      icon: Users,
      action: 'Join a caregiver support group or message one person who has walked this road. Connection heals.',
      cta: 'Find community',
      to: '/get-involved',
    },
    {
      feeling: 'Exhausted',
      icon: Moon,
      action: 'Ask for one hour of respite this week. Nap. Sit in silence. Your rest is care for your loved one.',
      cta: 'Talk to us',
      to: '/contact',
    },
    {
      feeling: 'Anxious',
      icon: Brain,
      action: 'Breathe in for 4 counts, hold for 4, out for 6. Repeat ten times. Then reach out for professional support.',
      cta: 'Counseling resources',
      to: '/resources',
    },
  ];

  const completedCount = Object.values(checked).filter(Boolean).length;

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative bg-[#5D4E6D] py-28 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-[28rem] h-[28rem] bg-[#E07A5F]/25 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-[#E07A5F]/20 text-[#E07A5F] border border-[#E07A5F]/30 mb-6 px-4 py-1">
              <Heart className="w-3 h-3 mr-2" />
              For Caregivers & Families
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1]">
              You carry so much.
              <span className="block text-[#E07A5F] mt-2">We carry you.</span>
            </h1>
            <p className="text-gray-200 text-lg sm:text-xl leading-relaxed">
              Behind every cancer patient is a circle of people who love them — spouses, parents, children,
              siblings, and friends who hold their world together. This space is built for you.
            </p>
          </div>
        </div>
      </section>

      {/* Acknowledgment Strip */}
      <section className="bg-[#F5F0EB] py-12 border-y border-[#5D4E6D]/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-8">
            <div className="text-center sm:text-left">
              <div className="text-4xl font-bold text-[#5D4E6D] mb-2">1 in 3</div>
              <p className="text-gray-600 text-sm">U.S. adults provide unpaid care to a loved one — caregivers are everywhere</p>
            </div>
            <div className="text-center sm:text-left border-l-0 sm:border-l border-[#5D4E6D]/15 sm:pl-8">
              <div className="text-4xl font-bold text-[#5D4E6D] mb-2">61%</div>
              <p className="text-gray-600 text-sm">of caregivers report high stress — your feelings are valid and shared</p>
            </div>
            <div className="text-center sm:text-left border-l-0 sm:border-l border-[#5D4E6D]/15 sm:pl-8">
              <div className="text-4xl font-bold text-[#5D4E6D] mb-2">100%</div>
              <p className="text-gray-600 text-sm">of caregivers deserve support — not just the patient they care for</p>
            </div>
          </div>
        </div>
      </section>

      {/* Five Pillars */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">How We Help</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4 mb-4">
              Five Pillars of Caregiver Support
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Drawing from City of Hope&apos;s caregiver framework and national best practices, we organize support
              around the five dimensions that matter most.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              const isLarge = idx === 0 || idx === 3;
              return (
                <Card
                  key={pillar.id}
                  data-testid={`caregiver-pillar-${pillar.id}`}
                  className={`group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden ${
                    isLarge ? 'lg:col-span-1' : ''
                  }`}
                >
                  <div className="h-1.5" style={{ backgroundColor: pillar.color }} />
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110"
                        style={{ backgroundColor: `${pillar.color}15` }}
                      >
                        <Icon className="w-7 h-7" style={{ color: pillar.color }} />
                      </div>
                      <span
                        className="text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full"
                        style={{ backgroundColor: `${pillar.color}10`, color: pillar.color }}
                      >
                        {pillar.tag}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-[#2D2A32] mb-3">{pillar.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">{pillar.description}</p>
                    <ul className="space-y-2.5">
                      {pillar.points.map((point) => (
                        <li key={point} className="flex items-start text-sm text-gray-700">
                          <Check className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" style={{ color: pillar.color }} />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* When You Feel... */}
      <section className="py-24 bg-[#F5F0EB] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-[#E07A5F]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#5D4E6D]/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">In The Moment</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4 mb-4">
              When You&apos;re Feeling...
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A small first step you can take right now, no matter what today looks like.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whenYouFeel.map((item) => {
              const Icon = item.icon;
              return (
                <Card
                  key={item.feeling}
                  data-testid={`caregiver-feeling-${item.feeling.toLowerCase()}`}
                  className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 group bg-white"
                >
                  <CardContent className="p-7">
                    <div className="w-12 h-12 bg-[#5D4E6D] rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#E07A5F] transition-colors duration-300">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-xs font-semibold text-[#E07A5F] uppercase tracking-wider mb-1">When you feel</p>
                    <h3 className="text-2xl font-bold text-[#2D2A32] mb-3">{item.feeling}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-5">{item.action}</p>
                    <Link
                      to={item.to}
                      className="inline-flex items-center text-[#5D4E6D] text-sm font-semibold group-hover:text-[#E07A5F] transition-colors"
                    >
                      {item.cta}
                      <ArrowRight className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Self-Care Checklist (Interactive) */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="lg:sticky lg:top-28">
              <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Try This Today</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4 mb-6 leading-tight">
                Your Daily Caregiver Self-Care Checklist
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Caring for someone with cancer is one of the hardest things you&apos;ll ever do. Use this list as a
                gentle reminder — not a to-do list to fail at, but a compass for tending to your own wellbeing.
              </p>

              <div className="bg-gradient-to-br from-[#5D4E6D] to-[#2D2A32] rounded-3xl p-8 text-white">
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles className="w-5 h-5 text-[#E07A5F]" />
                  <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Today&apos;s Progress</span>
                </div>
                <div className="flex items-end gap-3 mb-3">
                  <span className="text-5xl font-bold">{completedCount}</span>
                  <span className="text-gray-300 mb-2">of {checklistItems.length} cared-for moments</span>
                </div>
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#E07A5F] to-[#F4A78F] transition-all duration-500 rounded-full"
                    style={{ width: `${(completedCount / checklistItems.length) * 100}%` }}
                  />
                </div>
                {completedCount === checklistItems.length && (
                  <p className="mt-4 text-[#E07A5F] font-semibold">
                    🌺 You showed up for yourself today. That matters.
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {checklistItems.map((item) => {
                const Icon = item.icon;
                const isChecked = checked[item.id];
                return (
                  <button
                    key={item.id}
                    onClick={() => toggleCheck(item.id)}
                    data-testid={`checklist-item-${item.id}`}
                    className={`w-full text-left p-5 rounded-2xl border-2 transition-all duration-300 flex items-start gap-4 ${
                      isChecked
                        ? 'bg-[#E07A5F]/10 border-[#E07A5F] shadow-md'
                        : 'bg-[#F5F0EB] border-transparent hover:border-[#5D4E6D]/30 hover:shadow-md'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                        isChecked ? 'bg-[#E07A5F] text-white' : 'bg-white text-[#5D4E6D]'
                      }`}
                    >
                      {isChecked ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span
                      className={`flex-1 leading-relaxed pt-1.5 transition-colors ${
                        isChecked ? 'text-[#2D2A32] font-medium' : 'text-gray-700'
                      }`}
                    >
                      {item.text}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Quote / Voice */}
      <section className="py-24 bg-[#2D2A32] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#E07A5F]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#5D4E6D]/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Quote className="w-14 h-14 text-[#E07A5F] mx-auto mb-8 opacity-80" />
          <blockquote className="text-2xl sm:text-3xl lg:text-4xl font-light text-white leading-relaxed mb-8">
            &ldquo;Caregivers are the silent backbone of every cancer journey. When we lift them up,
            we lift up the patient, the family, and the whole community.&rdquo;
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-px bg-[#E07A5F]" />
            <p className="text-[#E07A5F] font-semibold uppercase tracking-wider text-sm">The Aloha Cancer Project</p>
            <div className="w-12 h-px bg-[#E07A5F]" />
          </div>
        </div>
      </section>

      {/* What Caregivers Need Most */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">If You Want to Help</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4 mb-6 leading-tight">
                How to Show Up for a Caregiver
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Friends and family often ask, <em>&ldquo;What can I do to help?&rdquo;</em> The most powerful gifts are
                rarely the big gestures — they&apos;re the small, consistent acts of care.
              </p>
              <p className="text-gray-600 leading-relaxed">
                If you know a caregiver, this list is your starting point. Pick one. Just one. And do it this week.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  icon: Calendar,
                  title: "Don't ask — offer specifics",
                  body: '"I\'m bringing dinner Tuesday at 6" beats "Let me know if you need anything."',
                },
                {
                  icon: Coffee,
                  title: 'Take over a small task',
                  body: 'Pick up groceries, walk the dog, do a laundry run, mow the lawn. Tiny weight lifted.',
                },
                {
                  icon: MessageCircle,
                  title: 'Listen without fixing',
                  body: 'Sometimes the most healing thing is presence. Hold space without trying to solve.',
                },
                {
                  icon: BookOpen,
                  title: 'Remember the patient — and the caregiver',
                  body: 'A text just for the caregiver, asking how THEY are, can mean the world.',
                },
              ].map((tip) => {
                const Icon = tip.icon;
                return (
                  <div
                    key={tip.title}
                    className="flex gap-5 p-6 bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow"
                  >
                    <div className="w-12 h-12 bg-[#E07A5F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#E07A5F]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#2D2A32] mb-1">{tip.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{tip.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#E07A5F] to-[#C96A50] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            You don&apos;t have to do this alone.
          </h2>
          <p className="text-white/90 text-lg sm:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
            Reach out — for resources, for a listening ear, or simply to be heard.
            We&apos;re here for the caregivers as much as we&apos;re here for the patients.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              data-testid="caregiver-cta-contact"
              className="bg-white text-[#E07A5F] hover:bg-gray-100 rounded-full px-8 py-6 text-base font-semibold transition-all duration-300"
            >
              <Link to="/contact">
                <Mail className="mr-2 w-5 h-5" />
                Reach Out to Us
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="caregiver-cta-resources"
              className="border-2 border-white text-white hover:bg-white hover:text-[#E07A5F] rounded-full px-8 py-6 text-base font-semibold transition-all duration-300 bg-transparent"
            >
              <Link to="/resources">
                Explore More Resources
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaregiversPage;
